import { type ReactNode, useEffect, useRef } from "react";
import { DialogShell } from "./DialogShell";
import { Button } from "./Button";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  isPending?: boolean;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  isPending = false,
  children,
}: ConfirmDialogProps) {
  // Synchronous double-fire guard: a fast double-click can trigger onConfirm
  // before React re-renders with isPending=true. The ref is set immediately
  // on the first click and reset when the dialog closes.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!open) firedRef.current = false;
  }, [open]);

  const handleConfirm = () => {
    if (firedRef.current || isPending) return;
    firedRef.current = true;
    onConfirm();
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      trigger={children}
    >
      {/* Phone: stacked full-width buttons, confirm on top. From sm: one row, right-aligned. */}
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? "destructive" : "primary"}
          onClick={handleConfirm}
          isLoading={isPending}
        >
          {isPending ? "Working…" : confirmLabel}
        </Button>
      </div>
    </DialogShell>
  );
}
